import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { observeIntersectionOnce, useIntersection } from '@flipster/react/misc/browser/intersection';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui/Price';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import { DateFormatter } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { competitionBunja } from '../../bunja';
import { RegisterNowButton } from '../../register-now-button';
import { UserInfo } from './user-info';

export const EventInfoSection = () => {
  const { competitionAtom, registrationStatusAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const registrationStatus = useAtomValue(registrationStatusAtom);
  const infoRef = useRef<HTMLElement>(null);
  useIntersection(
    infoRef,
    observeIntersectionOnce(() => {
      segment.track(CompetitionsEvent.CompetitionLandingViewPageScroll, {
        section_title: 'Info',
      });
    }),
  );
  if (!competition) return null;
  return (
    <section
      style={{
        backgroundImage: 'linear-gradient(94deg, var(--info-bg-left-color), var(--info-bg-right-color) 100%)',
      }}
      ref={infoRef}
    >
      <div
        className={flex({
          w: '100%',
          textStyle: 'Title/XS',
          tabletDown: {
            flexDir: 'column',
          },
          tablet: {
            maxW: 'desktopMaxWidth',
            m: '0 auto',
          },
          tabletOnly: {
            p: '0 36px',
          },
        })}
      >
        <EventInfo />
        {/* 등록 기간 && 등록하지 않음 */}
        {competition.competitionType != 'WEEKLY_PNL' &&
          registrationStatus?.isBetween &&
          !competition.participationTime && <RegisterNowButton />}
        {/* recurring tc OR 등록한 유저 */}
        {(competition.competitionType == 'WEEKLY_PNL' || competition.participationTime) && <UserInfo />}
      </div>
    </section>
  );
};

const EventInfo = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);

  if (!competition) return null;
  const totalTradingVolume =
    competition.competitionType === 'HOSTED_BY_INFLUENCER' ? competition.totalTradingVolume : undefined;

  return (
    <div
      className={flex({
        w: '100%',
        flexDir: 'column',
        color: 'content.default.level-5',
        p: {
          base: 16,
          tablet: '24px 0',
        },
        tablet: {
          justifyContent: 'center',
        },
      })}
      data-test-id='tc_desc_container'
    >
      <div
        className={stack({ flexDir: { base: 'column', tablet: 'row' }, mb: 16, gap: { base: 16, smallDesktop: 60 } })}
      >
        <dl>
          <dt className={css({ textStyle: 'Title/M' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Total_Prize_Pool' />
          </dt>
          <Price price={competition.prizePool} precision={0} currency={UsdtCurrency} fontType='numTitle' />
        </dl>
        {totalTradingVolume && (
          <dl>
            <dt className={css({ textStyle: 'Title/M' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Total_Trade_Volume' />
            </dt>
            <Price price={totalTradingVolume} precision={0} currency={UsdtCurrency} fontType='numTitle' />
          </dl>
        )}
      </div>
      <p className={css({ textStyle: 'Text/L/Regular' })}>
        Event duration:{' '}
        <Datetime value={competition.competitionStartTime} format={DateFormatter.ShortYearDateTime} isUTC />
        <span> - </span>
        <Datetime value={competition.competitionEndTime} format={DateFormatter.ShortYearDateTime} isUTC withUTCText />
      </p>
    </div>
  );
};
